/**
 * screens/guest.tsx — Guest mobile web (no app), QR self-join.
 * A REAL responsive web page (not the handoff's phone-frame mockup): full-bleed
 * on a phone, centered two-column on desktop — all content present at every size.
 * Flow: join → OTP (auto-submit on 6th digit) → live status, plus paused /
 * self-join-off / removed guard states. Public surface: no chrome, no auth.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  View, Text, ScrollView, TextInput, TouchableOpacity, Platform, useWindowDimensions,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import { useQueue } from '../queue/store';
import { positionOf, waitingFIFO } from '../queue/engine';
import { fmtWait, waitMin, money } from '../queue/format';
import { Mono, Stepper, StateChip } from '../queue/components/atoms';
import { stateStyle } from '../queue/stateStyle';
import { useGuestSession } from '../queue/guestSession';
import { useGuestFlow } from '../screens/hooks/useGuestFlow';
import type { EntryState } from '../queue/types';

export default function GuestScreen() {
  const { theme } = useTheme();
  const { colors } = theme;
  const store = useQueue();
  const { isPublic, queueId, queueInfo, authHeader } = useGuestSession();
  const { width } = useWindowDimensions();
  const wide = width > 720;

  // Public guest surface → live flow (Backbone guest token). Otherwise the
  // in-memory store's guest flow, unchanged.
  const useLive = isPublic && !!queueId;
  const liveFlow = useGuestFlow(useLive ? queueId : null, queueInfo, authHeader);
  const flow = useLive && liveFlow ? liveFlow : {
    g: store.state.guest,
    guestSet: store.guestSet, guestParty: store.guestParty, guestName: store.guestName, guestPhone: store.guestPhone,
    guestToggleConsent: store.guestToggleConsent, guestJoin: store.guestJoin,
    guestVerify: store.guestVerify, guestRemove: store.guestRemove, guestReset: store.guestReset,
  };
  const { g, guestSet, guestParty, guestName, guestPhone, guestToggleConsent, guestJoin, guestVerify, guestRemove, guestReset } = flow;

  // Gating fields: live from the resolved queue info, else the in-memory cfg2.
  // (Live has no deposit concept on the public link; deposit stays demo-only.)
  const cfg2 = useLive && queueInfo ? {
    self_join: queueInfo.selfJoinEnabled,
    accepting: queueInfo.acceptingEntries,
    self_removal: queueInfo.selfRemovalEnabled,
    maxParty: queueInfo.maxSelfJoinParty ?? 12,
    deposit: false,
    depositAmt: 0,
  } : store.state.cfg2;

  const otpRef = useRef<TextInput>(null);
  const [joinTouched, setJoinTouched] = useState(false);

  // Join validity: name required + phone exactly 10 digits (same rule as the
  // platform signup field) + DPDP consent. The guest becomes a CRM contact.
  const nameOk = g.name.trim().length > 0;
  const phoneOk = g.phone.replace(/\D/g, '').length === 10;

  // Derived line metrics (in-memory only; the live status step shows the
  // server's authoritative position instead of a client estimate).
  const fifo = waitingFIFO(store.state.entries);
  const liveWait = fmtWait(waitMin(fifo.length + 1));

  const stepJoin = g.step === 'join' && cfg2.self_join && cfg2.accepting;
  const guardPaused = g.step === 'join' && cfg2.self_join && !cfg2.accepting;
  const guardNoSelfJoin = g.step === 'join' && !cfg2.self_join;

  // Focus hidden OTP input when entering the OTP step.
  useEffect(() => {
    if (g.step === 'otp') {
      const t = setTimeout(() => otpRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [g.step]);

  function onJoin() {
    if (!nameOk || !phoneOk) { setJoinTouched(true); return; } // block until valid
    guestJoin();
  }
  function onOtpChange(v: string) {
    const otp = (v || '').replace(/\D/g, '').slice(0, 6);
    guestSet({ otp });
    // Pass the fresh code explicitly — the setTimeout closure's g.otp is stale.
    if (otp.length === 6) setTimeout(() => guestVerify(otp), 220);
  }

  // ── Guard cards ───────────────────────────────────────────────────────────
  const Guard = ({ glyph, title, body }: { glyph: string; title: string; body: string }) => (
    <View style={{ alignItems: 'center', paddingTop: 64 }}>
      <View style={{
        width: 64, height: 64, borderRadius: 32, borderWidth: 2, borderColor: colors.ink,
        alignItems: 'center', justifyContent: 'center', marginBottom: 22,
      }}>
        <Text style={{ fontSize: 26, color: colors.ink }}>{glyph}</Text>
      </View>
      <Text style={{ fontSize: 21, fontWeight: '600', color: colors.ink, marginBottom: 8 }}>{title}</Text>
      <Text style={{ fontSize: 14, color: colors.ink3, lineHeight: 21, maxWidth: 240, textAlign: 'center' }}>{body}</Text>
    </View>
  );

  let inner: React.ReactNode = null;

  if (guardPaused) {
    inner = <Guard glyph="⏸" title="Not taking new entries" body="The waitlist is paused right now. Please check back shortly or speak to the host." />;
  } else if (guardNoSelfJoin) {
    inner = <Guard glyph="☰" title="Please see the host" body="Self check-in isn't available here. Our host will add you to the line." />;
  } else if (g.step === 'removed') {
    inner = (
      <View style={{ alignItems: 'center', paddingTop: 64 }}>
        <View style={{
          width: 64, height: 64, borderRadius: 32, borderWidth: 2, borderColor: colors.ink,
          alignItems: 'center', justifyContent: 'center', marginBottom: 22,
        }}>
          <Text style={{ fontSize: 26, color: colors.ink }}>✓</Text>
        </View>
        <Text style={{ fontSize: 21, fontWeight: '600', color: colors.ink, marginBottom: 8 }}>You've left the line</Text>
        <Text style={{ fontSize: 14, color: colors.ink3, lineHeight: 21, maxWidth: 250, textAlign: 'center', marginBottom: 26 }}>
          No charge. We hope to see you another time.
        </Text>
        <TouchableOpacity onPress={guestReset} style={{
          height: 48, paddingHorizontal: 24, borderRadius: 13, borderWidth: 1.5, borderColor: colors.ink,
          backgroundColor: colors.ink, alignItems: 'center', justifyContent: 'center',
        }}>
          <Text style={{ color: colors.white, fontWeight: '600', fontSize: 14 }}>Join again</Text>
        </TouchableOpacity>
      </View>
    );
  } else if (stepJoin) {
    inner = (
      <View style={{ gap: 22 }}>
        <View>
          <Mono style={{ fontSize: 11, letterSpacing: 1.5, color: colors.ink3, textTransform: 'uppercase' }}>{useLive && queueInfo ? queueInfo.name : store.state.queueName}</Mono>
          <Text style={{ fontSize: 27, fontWeight: '700', lineHeight: 31, marginTop: 8, color: colors.ink }}>Join the waitlist</Text>
          <Text style={{ fontSize: 14, color: colors.ink3, marginTop: 6 }}>
            Live wait right now · <Mono style={{ color: colors.ink, fontWeight: '600' }}>{liveWait}</Mono> · {fifo.length}{fifo.length === 1 ? ' party' : ' parties'} ahead
          </Text>
        </View>

        <View>
          <Text style={{ fontSize: 13, fontWeight: '600', marginBottom: 10, color: colors.ink }}>How many in your party?</Text>
          <Stepper
            value={g.party} size="lg"
            onMinus={() => guestParty(-1)} onPlus={() => guestParty(1)}
            hint={'Max self-join party is ' + (cfg2.maxParty || 8)}
          />
        </View>

        <View>
          <Text style={{ fontSize: 13, fontWeight: '600', marginBottom: 8, color: colors.ink }}>Your name</Text>
          <TextInput
            value={g.name} onChangeText={guestName}
            placeholder="e.g. Priya Sharma" placeholderTextColor={colors.ink4}
            style={{ height: 54, borderWidth: 1.5, borderColor: colors.lineStrong, borderRadius: 14, backgroundColor: colors.card, paddingHorizontal: 16, fontSize: 17, color: colors.ink } as any}
          />
          {joinTouched && !nameOk && <Text style={{ fontSize: 12, color: colors.ink3, marginTop: 6 }}>Please enter your name.</Text>}
        </View>

        <View>
          <Text style={{ fontSize: 13, fontWeight: '600', marginBottom: 8, color: colors.ink }}>Mobile number</Text>
          <View style={{
            flexDirection: 'row', alignItems: 'center', height: 54, borderWidth: 1.5, borderColor: colors.lineStrong,
            borderRadius: 14, backgroundColor: colors.card, overflow: 'hidden',
          }}>
            <View style={{ paddingHorizontal: 14, borderRightWidth: 1, borderRightColor: colors.line, height: '100%', justifyContent: 'center' }}>
              <Mono style={{ fontSize: 16, color: colors.ink2 }}>+91</Mono>
            </View>
            <TextInput
              value={g.phone} onChangeText={guestPhone}
              keyboardType="number-pad" placeholder="9876543210" placeholderTextColor={colors.ink4}
              maxLength={10}
              style={{ flex: 1, fontFamily: 'IBM Plex Mono', fontSize: 18, paddingHorizontal: 14, color: colors.ink, height: '100%' } as any}
            />
          </View>
          {joinTouched && !phoneOk && <Text style={{ fontSize: 12, color: colors.ink3, marginTop: 6 }}>Enter a 10-digit mobile number.</Text>}
        </View>

        {cfg2.deposit && (
          <View style={{
            flexDirection: 'row', gap: 10, padding: 14, borderWidth: 1, borderColor: colors.lineStrong,
            borderStyle: 'dashed', borderRadius: 12, backgroundColor: colors.paper2,
          }}>
            <Text style={{ fontSize: 16 }}>◈</Text>
            <Text style={{ fontSize: 12, color: colors.ink2, lineHeight: 17, flex: 1 }}>
              A refundable <Mono style={{ fontWeight: '600' }}>{money(cfg2.depositAmt)}</Mono> hold confirms your spot. Charged only if you no-show.
            </Text>
          </View>
        )}

        <TouchableOpacity onPress={guestToggleConsent} style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
          <View style={{
            width: 18, height: 18, marginTop: 1, borderRadius: 4, borderWidth: 1.5, borderColor: colors.ink,
            backgroundColor: g.consent ? colors.ink : colors.card, alignItems: 'center', justifyContent: 'center',
          }}>
            {g.consent && <Text style={{ color: colors.white, fontSize: 12, lineHeight: 14 }}>✓</Text>}
          </View>
          <Text style={{ fontSize: 11.5, color: colors.ink3, lineHeight: 17, flex: 1 }}>
            I agree to be contacted by SMS about my place in line, and to the storage of my number for this visit per the <Text style={{ textDecorationLine: 'underline' }}>DPDP notice</Text>.
          </Text>
        </TouchableOpacity>

        <TouchableOpacity onPress={onJoin} style={{
          height: 56, borderRadius: 15, backgroundColor: colors.ink, alignItems: 'center', justifyContent: 'center',
        }}>
          <Text style={{ color: colors.white, fontWeight: '600', fontSize: 16 }}>
            {cfg2.deposit ? 'Pay ' + money(cfg2.depositAmt) + ' & join' : 'Join the waitlist'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  } else if (g.step === 'otp') {
    inner = (
      <View style={{ gap: 20, paddingTop: 8 }}>
        <TouchableOpacity onPress={() => guestSet({ step: 'join' })} style={{ alignSelf: 'flex-start' }}>
          <Text style={{ color: colors.ink3, fontSize: 13 }}>← Back</Text>
        </TouchableOpacity>
        <View>
          <Text style={{ fontSize: 25, fontWeight: '700', lineHeight: 29, color: colors.ink }}>Verify your number</Text>
          <Text style={{ fontSize: 14, color: colors.ink3, marginTop: 8 }}>
            We sent a 6-digit code to{'\n'}<Mono style={{ color: colors.ink, fontWeight: '600' }}>+91 {g.phone}</Mono>
          </Text>
        </View>
        {/* The hidden input OVERLAYS the box row at full size (transparent) so
            tapping anywhere on the boxes focuses it and typing fills them — the
            standard OTP pattern. (It was a 1px field parked top-left, which was
            untappable, so the code could never be entered.) */}
        <View style={{ position: 'relative' }}>
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'space-between' }}>
            {[0, 1, 2, 3, 4, 5].map(i => {
              const ch = g.otp[i] || '';
              const active = g.otp.length === i;
              return (
                <View key={i} style={{
                  flex: 1, height: 58, borderRadius: 13, borderWidth: 1.5,
                  borderColor: ch ? colors.ink : (active ? colors.ink3 : colors.lineStrong),
                  backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center',
                  ...(active ? (Platform.OS === 'web' ? { boxShadow: '0 0 0 3px #ECECEA' } as any : {}) : {}),
                }}>
                  <Mono style={{ fontSize: 26, fontWeight: '600', color: colors.ink }}>{ch}</Mono>
                </View>
              );
            })}
          </View>
          <TextInput
            ref={otpRef} value={g.otp} onChangeText={onOtpChange}
            keyboardType="number-pad" maxLength={6} autoFocus
            // Full-size transparent overlay: catches taps + keystrokes over the boxes.
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0,
              ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) }}
          />
        </View>
        <View style={{ gap: 10 }}>
          <TouchableOpacity onPress={() => otpRef.current?.focus()} style={{
            height: 50, borderRadius: 13, borderWidth: 1.5, borderColor: colors.ink, backgroundColor: colors.card,
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Text style={{ fontWeight: '600', fontSize: 15, color: colors.ink }}>Enter code</Text>
          </TouchableOpacity>
          <Text style={{ textAlign: 'center', fontSize: 12, color: colors.ink4 }}>Hint — any 6 digits work in this demo</Text>
        </View>
        <Text style={{ textAlign: 'center', fontSize: 13, color: colors.ink3 }}>
          Didn't get it? <Text style={{ textDecorationLine: 'underline', color: colors.ink }}>Resend in 0:23</Text>
        </Text>
      </View>
    );
  } else if (g.step === 'status' && g.entryId) {
    // Normalize the entry from whichever source: in-memory domain Entry, or the
    // live polled EntryWithEvents (server is authoritative; client position is
    // only meaningful in the in-memory demo).
    const le = useLive ? (liveFlow?.entry ?? null) : null;
    const me = useLive ? null : store.state.entries.find(x => x.id === g.entryId);
    const e = useLive
      ? (le ? {
          state: le.entry.currentState as EntryState,
          token: le.entry.id.replace(/-/g, '').slice(0, 6).toUpperCase(),
          party: le.entry.partySize ?? 0,
          // Prefer the server-resolved human label ("T4"); never show the raw
          // resource ULID to a guest (it overflowed the card). Fall back to '—'.
          allocatedResource: le.allocatedResourceLabel ?? null,
          events: le.events.map(ev => ({ type: ev.eventType })),
        } : null)
      : (me ? {
          state: me.state, token: me.token, party: me.party,
          allocatedResource: me.allocatedResource,
          events: me.events.map(ev => ({ type: ev.type })),
        } : null);
    if (e) {
      const st = stateStyle(e.state);
      // Live: server position lives in positionCache and may be null until the
      // engine computes it — don't fabricate "0 / -1 ahead" in that window.
      const pos = useLive
        ? (le?.entry.positionCache ?? null)
        : (positionOf(store.state.entries, (me as any).id) ?? 0);
      let bigCaption = 'Status', bigNumber = '—', bigSub = st.label, waitValue = '—';
      if (e.state === 'WAITING') {
        if (pos == null) {
          // Position not yet known — show an honest holding state, not a fake 0.
          bigCaption = 'Status'; bigNumber = '•'; bigSub = "You're in line";
          waitValue = '—';
        } else {
          const ahead = Math.max(0, pos - 1);
          bigCaption = 'Your position'; bigNumber = String(pos);
          bigSub = ahead === 0 ? "You're next" : `${ahead} ahead of you`;
          waitValue = fmtWait(waitMin(pos)).replace('~', '');
        }
      } else if (e.state === 'CALLED') {
        bigCaption = 'Your table'; bigNumber = e.allocatedResource || '—'; bigSub = 'Head to the host now'; waitValue = 'now';
      } else if (e.state === 'SEATED') {
        bigCaption = 'Seated at'; bigNumber = e.allocatedResource || '—'; bigSub = 'Enjoy your meal'; waitValue = '—';
      }
      const order: Array<'JOINED' | 'VERIFIED' | 'WAITING' | 'CALLED' | 'SEATED'> = ['JOINED', 'VERIFIED', 'WAITING', 'CALLED', 'SEATED'];
      const labels: Record<string, string> = {
        JOINED: 'Joined the line', VERIFIED: 'Number verified', WAITING: 'Waiting for a table',
        CALLED: "Table ready — you're next", SEATED: 'Seated',
      };
      const reached = new Set(e.events.map(ev => ev.type));
      const curIdx = order.indexOf(e.state === 'CALLED' ? 'CALLED' : (e.state === 'SEATED' ? 'SEATED' : 'WAITING'));
      const showRemove = cfg2.self_removal && (e.state === 'WAITING' || e.state === 'CALLED');

      inner = (
        <View style={{ gap: 18 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <StateChip state={e.state} compact />
            <Mono style={{ marginLeft: 'auto', fontSize: 11, color: colors.ink4 }}>#{e.token}</Mono>
          </View>

          <View style={{ backgroundColor: colors.ink, borderRadius: 22, padding: 30, alignItems: 'center', overflow: 'hidden', width: '100%' }}>
            <Mono style={{ fontSize: 11, letterSpacing: 1.8, color: colors.ink4, textTransform: 'uppercase' }}>{bigCaption}</Mono>
            {/* adjustsFontSizeToFit + numberOfLines keep the value INSIDE the card
                no matter how long it is (the raw resource ULID used to overflow
                across the page); overflow:hidden + width:100% on the card is the
                hard backstop. */}
            <Mono
              adjustsFontSizeToFit
              numberOfLines={1}
              style={{ fontSize: 84, fontWeight: '600', lineHeight: 92, color: colors.white, marginVertical: 8, textAlign: 'center', alignSelf: 'stretch' }}
            >{bigNumber}</Mono>
            <Text style={{ fontSize: 14, color: '#C9C9C6' }}>{bigSub}</Text>
          </View>

          <View style={{ flexDirection: 'row', gap: 10 }}>
            {[{ k: 'Est. wait', v: waitValue }, { k: 'Party', v: String(e.party) }].map(t => (
              <View key={t.k} style={{ flex: 1, borderWidth: 1, borderColor: colors.line, borderRadius: 14, padding: 14, backgroundColor: colors.card }}>
                <Text style={{ fontSize: 11, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t.k}</Text>
                <Mono style={{ fontSize: 22, fontWeight: '600', marginTop: 4, color: colors.ink }}>{t.v}</Mono>
              </View>
            ))}
          </View>

          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 14, padding: 16, backgroundColor: colors.card }}>
            <Text style={{ fontSize: 12, fontWeight: '600', marginBottom: 12, color: colors.ink }}>Your progress</Text>
            {order.map((k, i) => {
              const done = reached.has(k) || i < curIdx;
              const cur = i === curIdx;
              return (
                <View key={k} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 34 }}>
                  <View style={{ alignItems: 'center', alignSelf: 'stretch' }}>
                    <View style={{
                      width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center',
                      backgroundColor: done || cur ? colors.ink : colors.card,
                      borderWidth: done || cur ? 0 : 1.5, borderColor: colors.lineStrong,
                    }}>
                      {done && !cur ? <Text style={{ color: colors.white, fontSize: 12 }}>✓</Text> : null}
                    </View>
                    {i < order.length - 1 && (
                      <View style={{ width: 2, flex: 1, minHeight: 12, backgroundColor: done ? colors.ink : colors.line }} />
                    )}
                  </View>
                  <Text style={{
                    fontSize: 13,
                    fontWeight: cur ? '600' : '400',
                    color: cur ? colors.ink : (done ? colors.ink2 : colors.ink4),
                  }}>{labels[k]}</Text>
                </View>
              );
            })}
          </View>

          {showRemove && (
            <TouchableOpacity onPress={guestRemove} style={{
              height: 48, borderRadius: 13, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card,
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Text style={{ color: colors.ink3, fontWeight: '500', fontSize: 14 }}>Remove me from the line</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }
  }

  // Flow notes — shown on EVERY size (stacked under the form on phone, beside
  // it on desktop). Nothing is dropped on mobile; it just reflows.
  const notes = [
    { title: 'Two taps to join', body: 'Party size defaults to 2 with ± steppers — no keyboard. Country code is pre-filled. The whole join is party → phone → consent.' },
    { title: 'OTP auto-submits', body: 'Verification fires the moment the 6th digit lands — no submit button to hunt for.' },
    { title: 'One screen, always live', body: 'Position, deterministic wait, and progress update in place. State chip drives the whole screen: Waiting → You’re next → Seated.' },
  ];
  const NotesBlock = (
    <View style={{ gap: 14 }}>
      <Mono style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: colors.ink3 }}>Flow notes</Mono>
      {notes.map(n => (
        <View key={n.title} style={{ borderLeftWidth: 2, borderLeftColor: colors.ink, paddingLeft: 14, paddingVertical: 2 }}>
          <Text style={{ fontSize: 13, fontWeight: '600', marginBottom: 3, color: colors.ink }}>{n.title}</Text>
          <Text style={{ fontSize: 12.5, color: colors.ink3, lineHeight: 19 }}>{n.body}</Text>
        </View>
      ))}
    </View>
  );

  // ── Real responsive guest page (no phone-frame mockup) ─────────────────────
  // A guest opens this on their phone via a QR scan, so it's a normal web page —
  // never a stripped-down view. ALL content is present at every size:
  //  • phone   → full-bleed app; form first, flow-notes stacked beneath, all in
  //              one scroll; safe-area bottom padding so the CTA clears the home
  //              indicator.
  //  • desktop → the same content as two columns (form + notes) centered in a
  //              comfortable max-width, like the design reference.
  // The warm paper background extends edge-to-edge on every size.
  const SAFE_BOTTOM = Platform.OS === 'web' ? ('env(safe-area-inset-bottom)' as any) : 0;
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{
        flexGrow: 1,
        alignItems: 'center',
        paddingHorizontal: wide ? 24 : 20,
        paddingTop: wide ? 40 : 28,
        paddingBottom: 40,
      }}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <View
        style={{
          width: '100%',
          maxWidth: wide ? 880 : 460,
          flexDirection: wide ? 'row' : 'column',
          gap: wide ? 40 : 30,
          alignItems: 'flex-start',
          paddingBottom: SAFE_BOTTOM,
        }}
      >
        {/* Form column — capped so it stays phone-width even beside the notes. */}
        <View style={{ flex: wide ? undefined : 1, width: wide ? 420 : '100%', maxWidth: '100%' }}>
          {inner}
        </View>
        {/* Notes — beside on desktop, stacked below on phone (never removed). */}
        <View style={{ flex: wide ? 1 : undefined, width: wide ? undefined : '100%', paddingTop: wide ? 8 : 4 }}>
          {NotesBlock}
        </View>
      </View>
    </ScrollView>
  );
}
